import { useEffect, useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useQueryClient } from '@tanstack/react-query'
import { useOrganization } from '@/contexts/OrganizationContext'
import { useOrganizationProviders, type OrganizationProviderRow } from '@/hooks/useOrganizationProviders'
import { useProviderTypes } from '@/hooks/useProviderTypes'
import { useProviderActions } from '@/hooks/useProviderActions'
import { dashboardKeys } from '@/hooks/useDashboardStats'
import { decodeRelayId } from '@/utils/id'
import { AddProviderDialog, type AddProviderSurveyProfileType } from '@/components/providers/AddProviderDialog'
import { EditProviderDialog } from '@/components/providers/EditProviderDialog'
import { DeleteProviderDialog } from '@/components/providers/DeleteProviderDialog'
import { ManageProviderServicesDialog } from '@/components/providers/ManageProviderServicesDialog'
import { useProviderServiceActions } from '@/hooks/useProviderServices'
import { useAbility } from '@/ability/AbilityContext'
import { canManageProviderServices } from '@/ability/ability'
import {
  AlertCircle,
  CheckCircle,
  ClipboardList,
  MoreHorizontal,
  PlusCircle,
  Search,
  Star,
  Trash2,
  Users,
  XCircle,
  Eye,
} from 'lucide-react'
import { useLocalization } from '@/contexts/LocalizationContext'
import {
  getProviderTypeDisplay,
  getAvailabilityStatusDisplay,
  getProviderStatusDisplay,
} from '@/utils/providerLocalization'

function normalizeStatus(status?: string | null, statusDisplay?: string | null): string {
  return (statusDisplay || status || '').trim() || '—'
}

export function ProvidersPage() {
  const { t } = useLocalization()
  const getTypeDisplay = (p: OrganizationProviderRow) =>
    getProviderTypeDisplay(p.providerType, p.providerTypeDisplay, t)
  const getAvailabilityDisplay = (p: OrganizationProviderRow) =>
    getAvailabilityStatusDisplay(p.availabilityStatus, p.availabilityStatusDisplay, t)
  const getStatusDisplay = (p: OrganizationProviderRow) =>
    getProviderStatusDisplay(p.status, p.statusDisplay, t)
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()
  const { providers, isLoading, isError, error } = useOrganizationProviders()
  const { providerTypes: providerTypeOptions, getNameForCode } = useProviderTypes()
  const {
    updateProvider,
    deleteProvider,
    updateMutation,
    deleteMutation,
  } = useProviderActions()
  const { setProviderServices, isLoading: isSavingServices } = useProviderServiceActions()
  const ability = useAbility()
  // Gate emitted by the backend from ProfileCompany.is_manager / ops / staff.
  const canManageServices = canManageProviderServices(ability)

  // Filters
  const [search, setSearch] = useState('')
  const [availability, setAvailability] = useState<'all' | 'available' | 'unavailable'>('all')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')

  // Dialog states
  // Holds which "Add" variant is open (profile flow + fixed provider type); null = closed.
  const [addProviderConfig, setAddProviderConfig] = useState<{
    profileType: AddProviderSurveyProfileType
    defaultProviderType: string
  } | null>(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [servicesDialogOpen, setServicesDialogOpen] = useState(false)
  const [selectedProvider, setSelectedProvider] = useState<OrganizationProviderRow | null>(null)

  // Handlers
  const handleViewEdit = (provider: OrganizationProviderRow) => {
    setSelectedProvider(provider)
    setEditDialogOpen(true)
  }

  const handleDeleteClick = (provider: OrganizationProviderRow) => {
    setSelectedProvider(provider)
    setDeleteDialogOpen(true)
  }

  const handleManageServices = (provider: OrganizationProviderRow) => {
    setSelectedProvider(provider)
    setServicesDialogOpen(true)
  }

  /**
   * Count of services this provider is authorized to offer. A provider with zero receives
   * no dispatches at all, so it is called out rather than shown as a plain "0".
   */
  const renderServicesCount = (p: OrganizationProviderRow) => {
    const count = p.capableServices?.length ?? 0
    return (
      <Badge variant={count > 0 ? 'outline' : 'secondary'} className="text-xs">
        {count > 0 ? count : t('providerServices.none')}
      </Badge>
    )
  }

  /**
   * Single source for the per-row action menu — the mobile card list and the desktop
   * table both render it, so adding an item here adds it in both places.
   *
   * `stopPropagation` on the trigger matters on mobile: the whole card is clickable
   * and would otherwise open the edit dialog underneath the menu.
   */
  const renderRowActions = (p: OrganizationProviderRow) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-8 w-8 p-0 flex-shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="sr-only">{t('providers.openMenu')}</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleViewEdit(p)}>
          <Eye className="me-2 h-4 w-4" />
          {t('providers.viewEdit')}
        </DropdownMenuItem>
        {canManageServices && (
          <DropdownMenuItem onClick={() => handleManageServices(p)}>
            <ClipboardList className="me-2 h-4 w-4" />
            {t('providerServices.manageServices')}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={() => handleDeleteClick(p)}
          className="text-red-600 focus:text-red-600"
        >
          <Trash2 className="me-2 h-4 w-4" />
          {t('common.delete')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )

  // Keep selected provider in sync with refetched list so the open dialog shows updated data after save
  useEffect(() => {
    if ((!editDialogOpen && !servicesDialogOpen) || !selectedProvider?.id || !providers?.length) return
    const fresh = providers.find((p) => p.id === selectedProvider.id)
    if (fresh && fresh !== selectedProvider) {
      setSelectedProvider(fresh)
    }
  }, [editDialogOpen, servicesDialogOpen, selectedProvider?.id, providers])

  // Switching organizations invalidates the current selection — close anything open.
  useEffect(() => {
    setEditDialogOpen(false)
    setDeleteDialogOpen(false)
    setServicesDialogOpen(false)
    setSelectedProvider(null)
  }, [currentOrganization?.id])

  // Build the type filter options from the canonical providerTypes query (code + ordering),
  // scoped to codes actually present among the loaded providers so the filter only offers
  // useful options. `typeFilter` holds the code; labels come from our local i18n (falling
  // back to the query's name) via getProviderTypeDisplay.
  const typeFilterOptions = useMemo(() => {
    const present = new Set<string>()
    ;(providers || []).forEach((p) => {
      const code = (p.providerType || '').trim().toLowerCase()
      if (code) present.add(code)
    })

    const options: Array<{ code: string; label: string }> = []
    const seen = new Set<string>()
    // Canonical order first.
    providerTypeOptions.forEach((opt) => {
      if (present.has(opt.code)) {
        options.push({ code: opt.code, label: getProviderTypeDisplay(opt.code, opt.name, t) })
        seen.add(opt.code)
      }
    })
    // Any present codes the query didn't return (e.g. retired types) still get a filter entry.
    present.forEach((code) => {
      if (!seen.has(code)) {
        options.push({ code, label: getProviderTypeDisplay(code, getNameForCode(code), t) })
      }
    })
    return options
  }, [providers, providerTypeOptions, getNameForCode, t])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (providers || []).filter((p) => {
      // Search by name, phone, or email
      const name = (p.fullName || p.name || '').toLowerCase()
      const phone = (p.phoneNumber || '').toLowerCase()
      const email = (p.email || '').toLowerCase()
      const matchesSearch = !q || name.includes(q) || phone.includes(q) || email.includes(q)
      
      // Availability filter
      const matchesAvailability =
        availability === 'all' ||
        (availability === 'available' ? !!p.isAvailable : !p.isAvailable)
      
      // Status filter
      const statusKey = (p.status || p.statusDisplay || '').toLowerCase()
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' 
          ? statusKey.includes('active') && !statusKey.includes('inactive') 
          : statusKey.includes('inactive'))
      
      // Provider type filter (compare the stable code, not the localized display)
      const providerTypeCode = (p.providerType || '').trim().toLowerCase()
      const matchesType = typeFilter === 'all' || providerTypeCode === typeFilter
      
      return matchesSearch && matchesAvailability && matchesStatus && matchesType
    })
  }, [providers, search, availability, statusFilter, typeFilter])

  const renderStatusBadge = (p: OrganizationProviderRow) => {
    const statusText = getStatusDisplay(p)
    const key = (p.status || p.statusDisplay || '').toLowerCase()

    const variant =
      key.includes('active') ? 'default' : key.includes('inactive') ? 'secondary' : key.includes('suspend') ? 'destructive' : 'outline'

    return <Badge variant={variant as any}>{statusText}</Badge>
  }

  const renderRating = (p: OrganizationProviderRow) => {
    const rating = p.rating ?? p.averageRating
    if (rating == null || rating === 0) {
      return <span className="text-muted-foreground text-sm">{t('providers.noRating')}</span>
    }
    return (
      <div className="flex items-center gap-1">
        <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
        <span className="font-medium">{rating.toFixed(1)}</span>
        {p.totalReviews != null && (
          <span className="text-xs text-muted-foreground">({p.totalReviews})</span>
        )}
      </div>
    )
  }

  const renderLicenseStatus = (p: OrganizationProviderRow) => {
    if (p.isLicenseExpired) {
      return (
        <Badge variant="destructive" className="text-xs">
          <AlertCircle className="h-3 w-3 me-1" />
          {t('providers.expired')}
        </Badge>
      )
    }
    if (p.daysUntilLicenseExpiry != null && p.daysUntilLicenseExpiry <= 30) {
      return (
        <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-800">
          <AlertCircle className="h-3 w-3 me-1" />
          {t('providers.daysLeft', { days: p.daysUntilLicenseExpiry })}
        </Badge>
      )
    }
    if (p.licenseExpiryDate) {
      return (
        <Badge variant="outline" className="text-xs">
          <CheckCircle className="h-3 w-3 me-1 text-green-600" />
          {t('providers.valid')}
        </Badge>
      )
    }
    return <span className="text-muted-foreground text-xs">—</span>
  }

  const stats = useMemo(() => {
    const list = providers || []
    const total = list.length
    const available = list.filter((p) => !!p.isAvailable).length
    const unavailable = total - available
    const active = list.filter((p) => {
      const statusKey = (p.status || p.statusDisplay || '').toLowerCase()
      return statusKey.includes('active') && !statusKey.includes('inactive')
    }).length
    
    // Calculate average rating for providers who have ratings
    const providersWithRatings = list.filter((p) => p.rating != null && p.rating > 0)
    const avgRating = providersWithRatings.length > 0
      ? providersWithRatings.reduce((sum, p) => sum + (p.rating || 0), 0) / providersWithRatings.length
      : null
    
    // Count providers with expiring licenses (within 30 days)
    const expiringLicenses = list.filter((p) => {
      const days = p.daysUntilLicenseExpiry
      return days != null && days >= 0 && days <= 30
    }).length
    
    return { total, available, unavailable, active, avgRating, expiringLicenses }
  }, [providers])

  return (
    <div className="container mx-auto px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">
      <div className="flex items-start justify-between gap-3 sm:gap-4 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold">{t('providers.title')}</h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">
            {currentOrganization
              ? t('providers.providersFor', { name: currentOrganization.displayName || currentOrganization.name })
              : t('providers.selectOrganization')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
          <Button
            type="button"
            onClick={() =>
              setAddProviderConfig({ profileType: 'provider', defaultProviderType: 'partner' })
            }
            className="bg-gradient-to-r from-brand-primary to-brand-secondary hover:opacity-90 shadow-md flex-shrink-0"
            size="sm"
          >
            <PlusCircle className="h-4 w-4 me-2" />
            {t('providers.addProvider')}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              setAddProviderConfig({ profileType: 'provider', defaultProviderType: 'nonhealthcare' })
            }
            className="flex-shrink-0"
            size="sm"
          >
            <PlusCircle className="h-4 w-4 me-2" />
            {t('providers.addNonHealthcareProvider')}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="relative overflow-hidden border-0 shadow-sm">
          <div className="absolute inset-0 bg-gradient-to-br from-brand-primary/15 via-transparent to-transparent" />
          <CardContent className="relative p-4">
            <div className="text-sm font-semibold text-gray-700">{t('providers.totalProviders')}</div>
            <div className="mt-2 text-2xl font-bold">{isLoading ? '—' : stats.total}</div>
            <div className="mt-3 text-xs text-muted-foreground">{t('providers.allProviders')}</div>
          </CardContent>
        </Card>
        <Card className="relative overflow-hidden border-0 shadow-sm">
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/15 via-transparent to-transparent" />
          <CardContent className="relative p-4">
            <div className="text-sm font-semibold text-gray-700">{t('providers.available')}</div>
            <div className="mt-2 flex items-center justify-between">
              <div className="text-2xl font-bold">{isLoading ? '—' : stats.available}</div>
              <div className="h-8 w-8 rounded-lg bg-green-500/15 flex items-center justify-center">
                <CheckCircle className="h-4 w-4 text-green-700" />
              </div>
            </div>
            <div className="mt-3 text-xs text-muted-foreground">{t('providers.readyToTakeServices')}</div>
          </CardContent>
        </Card>
        <Card className="relative overflow-hidden border-0 shadow-sm">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-500/15 via-transparent to-transparent" />
          <CardContent className="relative p-4">
            <div className="text-sm font-semibold text-gray-700">{t('providers.unavailable')}</div>
            <div className="mt-2 flex items-center justify-between">
              <div className="text-2xl font-bold">{isLoading ? '—' : stats.unavailable}</div>
              <div className="h-8 w-8 rounded-lg bg-gray-500/15 flex items-center justify-center">
                <XCircle className="h-4 w-4 text-gray-700" />
              </div>
            </div>
            <div className="mt-3 text-xs text-muted-foreground">{t('providers.notAvailableRightNow')}</div>
          </CardContent>
        </Card>
        <Card className="relative overflow-hidden border-0 shadow-sm">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/15 via-transparent to-transparent" />
          <CardContent className="relative p-4">
            <div className="text-sm font-semibold text-gray-700">{t('providers.active')}</div>
            <div className="mt-2 text-2xl font-bold">{isLoading ? '—' : stats.active}</div>
            <div className="mt-3 text-xs text-muted-foreground">{t('providers.statusActive')}</div>
          </CardContent>
        </Card>
      </div>

      {isError && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="text-red-700">
              <div className="font-medium">{t('providers.failedToLoad')}</div>
              <div className="text-sm">{(error as Error)?.message || t('common.unknownError')}</div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-0 shadow-sm overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-gray-500" />
            <span className="font-bold">{t('providers.providers')}</span>
            <span className="ms-auto text-sm text-muted-foreground">{filtered.length}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-center sm:flex-wrap">
            <div className="relative flex-1 sm:max-w-sm">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder={t('providers.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="ps-10 bg-gray-50 border-gray-200"
                disabled={isLoading}
              />
            </div>
            <div className="flex flex-wrap gap-2 sm:gap-4">
              <select
                value={availability}
                onChange={(e) => setAvailability(e.target.value as any)}
                className="flex-1 sm:flex-none px-3 py-2 border border-input rounded-md bg-background disabled:opacity-50 text-sm min-w-0"
                disabled={isLoading}
              >
                <option value="all">{t('common.all')}</option>
                <option value="available">{t('providers.available')}</option>
                <option value="unavailable">{t('providers.unavailable')}</option>
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="flex-1 sm:flex-none px-3 py-2 border border-input rounded-md bg-background disabled:opacity-50 text-sm min-w-0"
                disabled={isLoading}
              >
                <option value="all">{t('providers.allStatus')}</option>
                <option value="active">{t('common.active')}</option>
                <option value="inactive">{t('common.inactive')}</option>
              </select>
              {typeFilterOptions.length > 0 && (
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="flex-1 sm:flex-none px-3 py-2 border border-input rounded-md bg-background disabled:opacity-50 text-sm min-w-0"
                  disabled={isLoading}
                >
                  <option value="all">{t('providers.allTypes')}</option>
                  {typeFilterOptions.map((opt) => (
                    <option key={opt.code} value={opt.code}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="rounded-lg border p-4">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                  </div>
                </div>
              ))
            ) : filtered.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                {t('providers.noProvidersFound')}
              </div>
            ) : (
              filtered.map((p) => (
                <div
                  key={p.id}
                  className="rounded-lg border bg-white p-4 hover:shadow-sm transition-shadow"
                  onClick={() => handleViewEdit(p)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <Avatar className="h-10 w-10 flex-shrink-0">
                        <AvatarFallback className="bg-gradient-to-br from-brand-primary to-brand-secondary text-white text-xs font-semibold">
                          {(p.fullName || p.name || 'P').trim().charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="font-medium truncate">{p.fullName || p.name || '—'}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {getTypeDisplay(p)}
                        </div>
                      </div>
                    </div>
                    {renderRowActions(p)}
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`h-2 w-2 rounded-full ${p.isAvailable ? 'bg-green-500' : 'bg-gray-300'}`}
                        aria-hidden
                      />
                      <Badge variant={p.isAvailable ? 'default' : 'secondary'} className="text-xs">
                        {getAvailabilityDisplay(p) !== '—' ? getAvailabilityDisplay(p) : (p.isAvailable ? t('providers.available') : t('providers.unavailable'))}
                      </Badge>
                    </div>
                    {renderStatusBadge(p)}
                    {renderServicesCount(p)}
                    {renderLicenseStatus(p)}
                  </div>
                  <div className="mt-2 flex items-center justify-between text-sm text-muted-foreground">
                    <span className="font-mono text-xs">{p.phoneNumber || '—'}</span>
                    {renderRating(p)}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('providers.name')}</TableHead>
                  <TableHead>{t('providers.type')}</TableHead>
                  <TableHead>{t('providers.phone')}</TableHead>
                  <TableHead>{t('providers.rating')}</TableHead>
                  <TableHead>{t('providers.availability')}</TableHead>
                  <TableHead>{t('providerServices.servicesColumn')}</TableHead>
                  <TableHead>{t('providers.license')}</TableHead>
                  <TableHead>{t('common.status')}</TableHead>
                  <TableHead className="w-[70px]">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Skeleton className="h-4 w-40" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-20" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-28" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-16" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-6 w-20" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-6 w-10" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-6 w-16" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-6 w-24" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-8 w-8" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-muted-foreground">
                      {t('providers.noProvidersFound')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((p) => (
                    <TableRow key={p.id} className="hover:bg-gray-50">
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-9 w-9">
                            <AvatarFallback className="bg-gradient-to-br from-brand-primary to-brand-secondary text-white text-xs font-semibold">
                              {(p.fullName || p.name || 'P').trim().charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <div className="truncate">{p.fullName || p.name || '—'}</div>
                            {p.email && (
                              <div className="text-xs text-muted-foreground truncate">{p.email}</div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {getTypeDisplay(p)}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{p.phoneNumber || '—'}</TableCell>
                      <TableCell>{renderRating(p)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span
                            className={`h-2 w-2 rounded-full ${p.isAvailable ? 'bg-green-500' : 'bg-gray-300'}`}
                            aria-hidden
                          />
                          <Badge variant={p.isAvailable ? 'default' : 'secondary'}>
                            {getAvailabilityDisplay(p) !== '—' ? getAvailabilityDisplay(p) : (p.isAvailable ? t('providers.available') : t('providers.unavailable'))}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>{renderServicesCount(p)}</TableCell>
                      <TableCell>{renderLicenseStatus(p)}</TableCell>
                      <TableCell>{renderStatusBadge(p)}</TableCell>
                      <TableCell>{renderRowActions(p)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* ── Dialogs ──────────────────────────────────────────────────── */}

      {/* Add Provider — uses same survey-based registration API as individual provider registration */}
      <AddProviderDialog
        open={!!addProviderConfig}
        onOpenChange={(open) => {
          if (!open) setAddProviderConfig(null)
        }}
        profileType={addProviderConfig?.profileType}
        defaultProviderType={addProviderConfig?.defaultProviderType}
        providersPageOrganizationEntityId={
          currentOrganization?.registrationDefaultEntityId ?? null
        }
        onSuccess={async () => {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: dashboardKeys.all }),
            queryClient.invalidateQueries({ queryKey: ['organizationProviders'] }),
          ])
        }}
      />

      {/* Edit / View Provider */}
      <EditProviderDialog
        provider={selectedProvider}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSubmit={async (providerId, input) => {
          // The provider id from GraphQL might be a Relay global ID.
          // The REST API expects a raw UUID.
          const uuid = decodeRelayId(providerId) ?? providerId
          await updateProvider(uuid, input)
        }}
        isLoading={updateMutation.isPending}
      />

      {/* Delete Provider */}
      <DeleteProviderDialog
        provider={selectedProvider}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={async (providerId) => {
          const uuid = decodeRelayId(providerId) ?? providerId
          await deleteProvider(uuid)
        }}
        isLoading={deleteMutation.isPending}
      />

      {/* Manage Services — corporate-manager per-provider service authorization */}
      <ManageProviderServicesDialog
        provider={selectedProvider}
        open={servicesDialogOpen}
        onOpenChange={setServicesDialogOpen}
        onSubmit={async (providerId, serviceTemplateIds) => {
          const uuid = decodeRelayId(providerId) ?? providerId
          await setProviderServices(uuid, serviceTemplateIds)
        }}
        isLoading={isSavingServices}
      />
    </div>
  )
}
